/**
 * The audit's Details tab: what this audit is — client, period, when it was
 * created — and which analyses it was set up to run. Status, data scope,
 * exceptions and the report each live on their own tab and are not repeated.
 */

// import { useState } from 'react'
import { Box } from '@mui/material';
// import { Button } from '@mui/material'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { Card, MetricCard, StatusBadge } from '../../shared/ui';
import { bytes, count, dateTime, date } from '../../utils/format';
import { financialYearLabel } from '../../utils/period';
import { PageHeader } from '../../provider/pageTitle';
import { useAnalysisOverviewQuery } from '../../queries/analysis';
import { DataTable, useFullScreen, type Column } from '../shared/tables';
import { useAuditContext } from './AuditWorkspace';
// import { EditAnalysesDialog } from './AnalysisPicker'
import type { AnalysisOverviewRow, AuditDetail } from '../../schemas';

function periodFYLabel(periodStart: string): string | null {
	const m = /^(\d{4})-(\d{2})/.exec(periodStart);
	if (!m) return null;
	const year = Number(m[1]);
	const month = Number(m[2]);
	return financialYearLabel(month >= 4 ? year : year - 1);
}

export default function AuditSettings() {
	const { audit, crumbs } = useAuditContext();
	const analyses = useAnalysisOverviewQuery(audit.pkAuditId);
	const fy = periodFYLabel(audit.periodStart);
	// The Edit analyses button and dialog are commented out below for now.
	// const [editing, setEditing] = useState(false)
	const full = useFullScreen('Chosen analyses');
	const filesFull = useFullScreen('Files');
	const deletedFiles = audit.files.filter((file) => file.deletedAt).length;

	/* The audit's files, a deleted one marked: the audit keeps the rows it
     froze from it, but it is gone from Data files. */
	const fileColumns: Array<Column<AuditDetail['files'][number]>> = [
		{
			key: 'name',
			label: 'File',
			width: 360,
			sortValue: (file) => file.originalName,
			render: (file) => file.originalName,
		},
		{
			key: 'rows',
			label: 'Rows',
			num: true,
			width: 100,
			sortValue: (file) => file.rowCount ?? -1,
			render: (file) =>
				file.rowCount !== null ? count(file.rowCount) : '—',
		},
		{
			key: 'size',
			label: 'Size',
			num: true,
			width: 100,
			sortValue: (file) => file.sizeBytes ?? 0,
			render: (file) => bytes(file.sizeBytes),
		},
		{
			key: 'status',
			label: 'Status',
			width: 200,
			sortValue: (file) => (file.deletedAt ? 'DELETED' : file.status),
			render: (file) =>
				file.deletedAt ? (
					<span
						className="bdg exception"
						title={`Deleted ${dateTime(file.deletedAt)}`}
					>
						Deleted
					</span>
				) : (
					<StatusBadge group="fileStatus" value={file.status} />
				),
		},
	];
	// const running = (analyses.data?.rows ?? []).some(
	//   (row) => row.status === 'RUNNING' || row.status === 'QUEUED',
	// )
	// const editBlocked = analyses.data?.locked
	//   ? 'The report has been issued, so the analyses can no longer change.'
	//   : running
	//     ? 'Wait for the running analyses to finish.'
	//     : undefined

	const columns: Array<Column<AnalysisOverviewRow>> = [
		{
			key: 'ref',
			label: 'Ref.',
			width: 75,
			sortValue: (row) => row.ref,
			render: (row) => row.ref,
		},
		{
			key: 'name',
			label: 'Analysis',
			width: 320,
			sortValue: (row) => row.name,
			render: (row) => row.name,
		},
		{
			key: 'description',
			label: 'What it checks',
			width: 460,
			render: (row) => (
				<span className="small mut">{row.description}</span>
			),
		},
	];

	return (
		<div className="stack">
			<PageHeader breadcrumbs={crumbs} title="Details" />

			{/* What this audit is, one card per fact. */}
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
					gap: 'var(--s3)',
				}}
			>
				<MetricCard
					small
					label="Audit"
					tone="accent"
					icon={<AssignmentOutlinedIcon />}
					value={audit.name}
				/>
				<MetricCard
					small
					label="Client"
					tone="info"
					icon={<BusinessOutlinedIcon />}
					value={audit.clientName}
					meta={audit.clientCode}
				/>
				<MetricCard
					small
					label="Period"
					tone="ok"
					icon={<CalendarMonthOutlinedIcon />}
					value={
						fy ??
						`${date(audit.periodStart)} – ${date(audit.periodEnd)}`
					}
					meta={
						fy
							? `${date(audit.periodStart)} – ${date(audit.periodEnd)}`
							: undefined
					}
				/>
				<MetricCard
					small
					label="Status"
					tone="warn"
					icon={<FlagOutlinedIcon />}
					value={
						<StatusBadge group="auditStatus" value={audit.status} />
					}
				/>
				<MetricCard
					small
					label="Created"
					icon={<ScheduleOutlinedIcon />}
					value={dateTime(audit.createdAt)}
				/>
			</Box>

			<Card
				title={`Files (${count(audit.files.length)})`}
				actions={filesFull.button}
			>
				{deletedFiles > 0 && (
					<div className="card-bd small mut">
						{deletedFiles === 1
							? '1 file was'
							: `${count(deletedFiles)} files were`}{' '}
						deleted from Data files after this audit used{' '}
						{deletedFiles === 1 ? 'it' : 'them'}. This audit keeps
						the rows it took, so its analyses and results are
						unchanged.
					</div>
				)}
				<div className="card-bd flush">
					<DataTable
						rows={audit.files}
						columns={fileColumns}
						rowKey={(file) => file.pkFileId}
						caption="Files"
						fullScreen={filesFull}
						maxHeight="short"
						empty="No files."
					/>
				</div>
			</Card>

			<Card
				title="Chosen analyses"
				actions={full.button}
				// actions={
				//   <Button
				//     variant="outlined"
				//     size="small"
				//     disabled={!analyses.data || Boolean(editBlocked)}
				//     title={editBlocked}
				//     onClick={() => setEditing(true)}
				//   >
				//     Edit analyses
				//   </Button>
				// }
			>
				<div className="card-bd flush">
					<DataTable
						rows={analyses.data?.rows ?? []}
						columns={columns}
						rowKey={(row) => row.slug}
						caption="Chosen analyses"
						loading={analyses.isPending}
						initialSort={{ key: 'ref', direction: 'asc' }}
						fullScreen={full}
					/>
				</div>
			</Card>

			{/* <EditAnalysesDialog
        open={editing}
        auditId={audit.pkAuditId}
        onClose={() => setEditing(false)}
      /> */}
		</div>
	);
}
